/**
 * Salão de mesas.
 *
 * A diferença entre isto e a fila é o que se escolhe. Na fila escolhe-se
 * quando; no salão, com quem. Por isso `sentar` leva o `hostId` e não um
 * jogo — o botão promete uma pessoa, e o servidor entrega aquela pessoa
 * ou recusa dizendo que ela já não está lá.
 */
import type { GameType } from './adapter.ts';
import type { PerfilPublico } from './perfil.ts';
import { baseDaApi } from './presenca.ts';

export interface MesaAberta {
  readonly hostId: string;
  readonly gameType: GameType;
  /** Nulo quando quem abriu a mesa prefere combinar o valor ali. */
  readonly sugestaoCents: number | null;
  readonly rakeBps: number;
  readonly esperandoDesde: string;
  readonly souEu: boolean;
  readonly jogador: PerfilPublico;
}

export interface MesaOcupada {
  readonly matchId: string;
  readonly gameType: GameType;
  readonly status: string;
  readonly desde: string;
  readonly jogadores: readonly PerfilPublico[];
}

export interface Salao {
  readonly abertas: readonly MesaAberta[];
  readonly ocupadas: readonly MesaOcupada[];
}

export type ResultadoSalao<T> =
  | { readonly estado: 'ok'; readonly dado: T }
  | { readonly estado: 'recusado'; readonly codigo: string; readonly mensagem: string }
  | { readonly estado: 'indisponivel' };

const MOTIVOS: Record<string, string> = {
  TABLE_TAKEN: 'Alguém sentou nessa mesa antes de você. Escolha outra.',
  CANNOT_SIT_AT_OWN_TABLE: 'Essa mesa é sua.',
  ALREADY_IN_MATCH: 'Você já está numa partida aberta.',
  INSUFFICIENT_FUNDS: 'Seu saldo não cobre a aposta mínima.',
  SELF_EXCLUDED: 'Sua conta está em autoexclusão.',
  MINOR_BLOCKED: 'Apostas com valor real exigem 18 anos.',
  ACCOUNT_BLOCKED: 'Conta bloqueada. Fale com o suporte.',
};

export async function carregarSalao(
  token: string,
  stakeMode: 'REAL' | 'VIRTUAL',
): Promise<ResultadoSalao<Salao>> {
  const base = baseDaApi();
  if (!base) return { estado: 'indisponivel' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/tables?stakeMode=${stakeMode}`, {
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    return { estado: 'indisponivel' };
  }

  if (resposta.status !== 200) return { estado: 'indisponivel' };
  return { estado: 'ok', dado: (await resposta.json()) as Salao };
}

/** Senta na mesa de alguém. Devolve o id da partida formada. */
export async function sentar(
  token: string,
  hostId: string,
): Promise<ResultadoSalao<{ readonly matchId: string }>> {
  const base = baseDaApi();
  if (!base) return { estado: 'indisponivel' };

  let resposta: Response;
  try {
    resposta = await fetch(`${base}/api/tables`, {
      method: 'POST',
      headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ hostId }),
    });
  } catch {
    return { estado: 'indisponivel' };
  }

  if (resposta.status === 200) {
    return { estado: 'ok', dado: (await resposta.json()) as { matchId: string } };
  }

  const corpo = (await resposta.json().catch(() => ({}))) as { error?: unknown };
  const codigo = typeof corpo.error === 'string' ? corpo.error : 'RECUSADO';
  return {
    estado: 'recusado',
    codigo,
    mensagem: MOTIVOS[codigo] ?? 'Não foi possível sentar nessa mesa.',
  };
}

/** "há 2 min", "agora mesmo". Tempo de espera lido de relance. */
export function esperandoHa(iso: string, agora: number): string {
  const segundos = Math.max(0, Math.floor((agora - Date.parse(iso)) / 1000));
  if (segundos < 45) return 'agora mesmo';
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  return `há ${horas} h`;
}
